import React, { useCallback, useContext, useMemo } from 'react';

import { ConfluenceDependenciesContext } from '@atlassian/company-hub-common';
import type { ConfluenceDependenciesProviderProps } from '@atlassian/company-hub-common';

import {
	createRendererMediaProvider,
	createEditorMediaProvider,
	getAttachmentSizeLimitErrorFlag,
} from '@confluence/fabric-media-support';
import { ErrorBoundary, Attribution } from '@confluence/error-boundary';
import { ADVANCED_SEARCH, PEOPLE_DIRECTORY } from '@confluence/named-routes';
import { useRouteDataRef } from '@confluence/route-manager/entry-points/RouteState';
import {
	MacroExperienceStop,
	getMacroAttributesFromADFNode,
	getExperienceName,
	MacroExperienceSuccess,
} from '@confluence/macro-tracker';
import { useSessionData } from '@confluence/session-data';
import {
	CARDS_IMAGE_UPLOAD_EXPERIENCE,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker';
import { markErrorAsHandled } from '@confluence/graphql';
import { getMonitoringClient } from '@confluence/monitoring';
import { withFlags } from '@confluence/flags';
import type { WithFlagsProps } from '@confluence/flags';
import { LazyEmojiComponentLoader } from '@confluence/emoji-title';
import type { FeedbackType } from '@confluence/company-hub-utils/entry-points/FeedbackCollectorLoader';
import { useFeedbackCollector } from '@confluence/company-hub-utils/entry-points/FeedbackCollectorLoader';
import { ExtensionConfigUpsellLoader } from '@confluence/change-edition/entry-points/PremiumExtensionsUpsell';
import { ShortcutListener } from '@confluence/shortcuts';
import { getSavedThemeLocal } from '@confluence/theming/entry-points/getSavedThemeLocal';

import { usePremiumGate } from '../shared-components/usePremiumGate';
import { CustomSitesRenderedExtension } from '../shared-components/CustomSitesRenderedExtension';
import { CustomSitesExtension } from '../shared-components/CustomSitesExtension';
import { useGetSpaceIdsFromSpaceKeys } from '../shared-components/useGetSpaceIdsFromSpaceKeys';

import { useSegmentationTargetGroups } from './use-segmentation-target-groups/useSegmentationTargetGroups';

const useFeedbackCollectorWrapper = ({ source, type }: { source: string; type: FeedbackType }) => {
	return useFeedbackCollector({ source, type });
};

const useSegmentationTargetGroupsWrapper = ({
	nextCursor,
	initialGroupIds = [],
}: {
	initialGroupIds?: string[];
	nextCursor?: string;
}) => {
	return useSegmentationTargetGroups({ nextCursor, initialGroupIds });
};

const ShortcutListenerWrapper = ({
	accelerator,
	listener,
}: {
	accelerator: string;
	listener: () => void;
}) => {
	return React.createElement(ShortcutListener, { accelerator, listener });
};

const ExtensionConfigUpsellLoaderWrapper = ({ feature }: { feature: string }) => {
	return React.createElement(ExtensionConfigUpsellLoader, {
		feature: feature as
			| 'premiumCardsConfig'
			| 'premiumCarouselConfig'
			| 'premiumSpotlightConfig'
			| 'premiumSearchConfig',
	});
};

const submitError = (error: Error) => {
	getMonitoringClient().submitError(error, {
		attribution: Attribution.COMPANY_HUB,
	});
	markErrorAsHandled(error);
};

export const useConfluenceMediaUploadExperience = () => {
	const experienceTracker = useContext(ExperienceTrackerContext);

	const start = useCallback(
		({ analyticsSource }: { analyticsSource: string }) => {
			experienceTracker.start({
				name: CARDS_IMAGE_UPLOAD_EXPERIENCE,
				attributes: {
					source: analyticsSource,
				},
			});
		},
		[experienceTracker],
	);

	const succeed = useCallback(() => {
		experienceTracker.succeed({ name: CARDS_IMAGE_UPLOAD_EXPERIENCE });
	}, [experienceTracker]);

	const stopOnError = useCallback(
		({ error }: { error: Error }) => {
			experienceTracker.stopOnError({ name: CARDS_IMAGE_UPLOAD_EXPERIENCE, error });
		},
		[experienceTracker],
	);

	return {
		start,
		succeed,
		stopOnError,
	};
};

export const ConfluenceDependenciesProviderComponent = ({
	mode,
	extensionNode,
	contentId = '',
	children,
	flags,
}: ConfluenceDependenciesProviderProps & WithFlagsProps) => {
	const getSpaceIdsFromSpaceKeys = useGetSpaceIdsFromSpaceKeys();
	const mediaUploadExperienceTracker = useConfluenceMediaUploadExperience();
	const sessionData = useSessionData();
	const { cloudId, displayName, isAdminHubAIEnabled, orgId, userId } = sessionData || {};
	const currentDataRefHref = useRouteDataRef().current?.href;

	const experienceStartComponent = useMemo(() => {
		const Component = mode === 'editor' ? CustomSitesExtension : CustomSitesRenderedExtension;
		return ({ children }: { children?: React.ReactNode }) => (
			<Component
				extensionNode={extensionNode!}
				contentId={contentId}
				showNoDataState={mode === 'editor' && !extensionNode}
			>
				{children}
			</Component>
		);
	}, [mode, extensionNode, contentId]);

	const experienceSuccessComponent = useCallback(() => {
		if (!extensionNode) return null;
		return (
			<MacroExperienceSuccess
				name={getExperienceName(mode, extensionNode)}
				contentId={contentId}
				extensionKey={extensionNode.extensionKey}
				mode={mode}
				attributes={getMacroAttributesFromADFNode(extensionNode)}
			/>
		);
	}, [extensionNode, contentId, mode]);

	const experienceStopComponent = useCallback(
		({ error }: { error?: Error }) => {
			if (!extensionNode) return null;
			return (
				<MacroExperienceStop
					error={error}
					name={getExperienceName(mode, extensionNode)}
					contentId={contentId}
					extensionKey={extensionNode.extensionKey}
					mode={mode}
					attributes={getMacroAttributesFromADFNode(extensionNode)}
				/>
			);
		},
		[mode, extensionNode, contentId],
	);

	const errorBoundaryComponent = useMemo(
		() =>
			({ children }: { children?: React.ReactNode }) => (
				<ErrorBoundary attribution={Attribution.COMPANY_HUB}>{children}</ErrorBoundary>
			),
		[],
	);

	const value = useMemo(
		() => ({
			experienceSuccessComponent,
			experienceStopComponent,
			cloudId,
			displayName: displayName || '',
			isAdminHubAIEnabled,
			orgId: orgId || '',
			userId: userId || '',
			advancedSearchUrl: ADVANCED_SEARCH.toUrl(),
			peopleDirectoryUrl: PEOPLE_DIRECTORY.toUrl(),
			getSpaceIdsFromSpaceKeys,
			errorBoundaryComponent,
			experienceStartComponent,
			createRendererMediaProvider,
			submitError,
			createEditorMediaProvider,
			getAttachmentSizeLimitErrorFlag,
			mediaUploadExperienceTracker,
			currentDataRefHref,
			useFeedbackCollector: useFeedbackCollectorWrapper,
			lazyEmojiComponentLoader: LazyEmojiComponentLoader,
			usePremiumGate,
			shortcutListener: ShortcutListenerWrapper,
			ExtensionConfigUpsellLoader: ExtensionConfigUpsellLoaderWrapper,
			flags,
			useSegmentationTargetGroups: useSegmentationTargetGroupsWrapper,
			getSavedThemeLocal,
		}),
		[
			experienceSuccessComponent,
			experienceStopComponent,
			cloudId,
			displayName,
			isAdminHubAIEnabled,
			orgId,
			userId,
			errorBoundaryComponent,
			experienceStartComponent,
			getSpaceIdsFromSpaceKeys,
			mediaUploadExperienceTracker,
			currentDataRefHref,
			flags,
		],
	);

	return (
		<ConfluenceDependenciesContext.Provider value={value}>
			{children}
		</ConfluenceDependenciesContext.Provider>
	);
};

export const ConfluenceDependenciesProvider = withFlags(
	({ children, ...props }: ConfluenceDependenciesProviderProps & WithFlagsProps) => (
		<ConfluenceDependenciesProviderComponent {...props}>
			{children}
		</ConfluenceDependenciesProviderComponent>
	),
);
